import React, { useEffect, useRef, useState } from 'react';
import { Box, IconButton, Tooltip, Menu, MenuItem, Divider } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  FormatAlignLeft,
  FormatAlignCenter,
  FormatAlignRight,
  Link as LinkIcon,
  Image as ImageIcon,
  OndemandVideo as YouTubeIcon,
  FormatQuote as QuoteIcon,
  HorizontalRule as HorizontalRuleIcon,
  FormatColorText as ColorTextIcon,
  Clear as ClearIcon,
  Title as HeadingIcon,
} from '@mui/icons-material';

import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import YouTube from '@tiptap/extension-youtube';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import MediaPicker from './MediaPicker';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
}

// Strip black inline colors so they read correctly on the dark editor background
const sanitizeColors = (html: string): string =>
  html.replace(/color:\s*(#000(000)?|rgb\(0,\s*0,\s*0\)|black)\b/gi, 'color: inherit');

// Groups icons so they wrap as a unit, never orphaning a single icon
const ToolbarGroup: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
    {children}
  </Box>
);

const ToolbarDivider = () => (
  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
);

const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Underline,
      Link.configure({ openOnClick: true }),
      Image,
      YouTube.configure({ controls: true, modestBranding: true }),
      TextStyle,
      Color,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      onChange(sanitizeColors(editor.getHTML()));
    },
    editorProps: {
      attributes: { class: 'tiptap-content' },
      transformPastedHTML(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        doc.querySelectorAll('*').forEach(el => {
          el.removeAttribute('style');
          el.removeAttribute('class');
          el.removeAttribute('id');
          // <font> attributes Word/Docs use
          el.removeAttribute('color');
          el.removeAttribute('face');
          el.removeAttribute('size');
        });
        return doc.body.innerHTML;
      },
    },
  });

  const [pickerOpen, setPickerOpen] = useState(false);
  const [headingAnchor, setHeadingAnchor] = useState<null | HTMLElement>(null);
  const colorInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editor) return;
    const currentContent = editor.getHTML();
    if (value && value !== currentContent) {
      editor.commands.setContent(sanitizeColors(value));
    }
  }, [editor, value]);

  if (!editor) return null;

  const btn = (active?: boolean) => ({
    size: 'small' as const,
    color: active ? 'primary' as const : 'default' as const,
    sx: { p: '4px' },
  });

  return (
    <Box sx={{ border: '1px solid #3A9188', borderRadius: 1, overflow: 'hidden' }}>
      {/* Toolbar */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 0.25,
          p: 0.75,
          borderBottom: '1px solid #3A9188',
          bgcolor: 'rgba(0,0,0,0.15)',
        }}
      >
        {/* Headings */}
        <ToolbarGroup>
          <Tooltip title="Headings">
            <IconButton {...btn()} onClick={(e) => setHeadingAnchor(e.currentTarget)}>
              <HeadingIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Menu anchorEl={headingAnchor} open={Boolean(headingAnchor)} onClose={() => setHeadingAnchor(null)}>
            {[1, 2, 3].map((level) => (
              <MenuItem
                key={level}
                onClick={() => {
                  editor.chain().focus().toggleHeading({ level: level as 1 | 2 | 3 }).run();
                  setHeadingAnchor(null);
                }}
              >
                Heading {level}
              </MenuItem>
            ))}
          </Menu>
        </ToolbarGroup>

        <ToolbarDivider />

        {/* Text formatting */}
        <ToolbarGroup>
          <Tooltip title="Bold">
            <IconButton {...btn(editor.isActive('bold'))} onClick={() => editor.chain().focus().toggleBold().run()}>
              <FormatBold fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Italic">
            <IconButton {...btn(editor.isActive('italic'))} onClick={() => editor.chain().focus().toggleItalic().run()}>
              <FormatItalic fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Underline">
            <IconButton {...btn(editor.isActive('underline'))} onClick={() => editor.chain().focus().toggleUnderline().run()}>
              <FormatUnderlined fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        {/* Color + Clear */}
        <ToolbarGroup>
          <Tooltip title="Text Color">
            <IconButton {...btn()} onClick={() => colorInputRef.current?.click()}>
              <ColorTextIcon fontSize="small" />
              <input
                ref={colorInputRef}
                type="color"
                defaultValue="#ffffff"
                style={{ position: 'absolute', opacity: 0, width: 0, height: 0, pointerEvents: 'none' }}
                onChange={(e) => editor.chain().focus().setColor(e.target.value).run()}
              />
            </IconButton>
          </Tooltip>
          <Tooltip title="Clear Formatting">
            <IconButton {...btn()} onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        {/* Lists */}
        <ToolbarGroup>
          <Tooltip title="Bullet List">
            <IconButton {...btn(editor.isActive('bulletList'))} onClick={() => editor.chain().focus().toggleBulletList().run()}>
              <FormatListBulleted fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Numbered List">
            <IconButton {...btn(editor.isActive('orderedList'))} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
              <FormatListNumbered fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        {/* Alignment */}
        <ToolbarGroup>
          <Tooltip title="Align Left">
            <IconButton {...btn()} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
              <FormatAlignLeft fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Center">
            <IconButton {...btn()} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
              <FormatAlignCenter fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Align Right">
            <IconButton {...btn()} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
              <FormatAlignRight fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        {/* Media */}
        <ToolbarGroup>
          <Tooltip title="Insert Link">
            <IconButton
              {...btn()}
              onClick={() => {
                const url = window.prompt('Enter URL');
                if (url) editor.chain().focus().setLink({ href: url }).run();
              }}
            >
              <LinkIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert Image">
            <IconButton {...btn()} onClick={() => setPickerOpen(true)}>
              <ImageIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Insert YouTube Video">
            <IconButton
              {...btn()}
              onClick={() => {
                const url = window.prompt('Enter YouTube URL');
                if (url) editor.commands.setYoutubeVideo({ src: url });
              }}
            >
              <YouTubeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToolbarGroup>

        <ToolbarDivider />

        {/* Structural */}
        <ToolbarGroup>
          <Tooltip title="Blockquote">
            <IconButton {...btn()} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
              <QuoteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Horizontal Rule">
            <IconButton {...btn()} onClick={() => editor.chain().focus().setHorizontalRule().run()}>
              <HorizontalRuleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </ToolbarGroup>
      </Box>

      <MediaPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(url) => {
          editor.chain().focus().setImage({ src: url }).run();
          setPickerOpen(false);
        }}
      />

      {/* Editor content */}
      <Box
        sx={{
          '& .tiptap-content': {
            minHeight: 200,
            color: '#fff',
            p: 1.5,
            outline: 'none',
            '& h1': { fontSize: '2rem', fontWeight: 700, mb: 1 },
            '& h2': { fontSize: '1.5rem', fontWeight: 700, mb: 1 },
            '& h3': { fontSize: '1.25rem', fontWeight: 700, mb: 1 },
            '& p': { mb: 1 },
            '& img': { maxWidth: '100%', borderRadius: 1, my: 1 },
            '& iframe': { width: '100%', borderRadius: 1 },
            '& blockquote': {
              borderLeft: '4px solid #00a3d7',
              paddingLeft: '16px',
              color: '#ccc',
              fontStyle: 'italic',
            },
            '& hr': {
              border: 'none',
              borderTop: '1px solid rgba(255,255,255,0.2)',
              margin: '16px 0',
            },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};

export default RichTextEditor;
